import { gql, useApolloClient, useMutation } from '@apollo/client';
import {
  Avatar,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardHeaderControl,
  CardSection,
  CardSectionControl,
  CardSectionDescription,
  CardSectionTitle,
  CardTitle,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Group,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Textarea,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { ChangeEvent, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Form } from 'src/components/common/form/Form';
import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { AvatarModal } from 'src/components/common/uploadAvatar/AvatarModal';
import { URLModal } from 'src/components/common/URLModal';
import {
  ApolloErrorMessage,
  ErrorMessage,
} from 'src/components/errorMessage/ErrorMessage';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { Config } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { DescriptionMarkdownRenderer } from '../../documentationPage/schemaPage/descriptionMarkdownRenderer/DescriptionMarkdownRenderer';
import { useSchema } from '../../hooks/useSchema';
import {
  ConfigureBuildPipelineModal,
  configureBuildPipelineModalFragment,
} from '../../variantSettingsPage/variantSettingsCards/configureBuildPipelineModal/ConfigureBuildPipelineModal';

export const GeneralGraphSettingsCardFragment = gql`
  fragment GeneralGraphSettingsCardFragment on Service {
    id
    description
    title
    avatarUrl
    defaultBuildPipelineTrack
    defaultBuildPipelineTrackUpdatedAt
    graphType
    avatarUpload {
      ... on MediaUploadInfo {
        csrfToken
        maxContentLength
        url
      }
      ... on AvatarUploadError {
        clientMessage
        code
        serverMessage
      }
    }
    roles {
      canManageBuildConfig
    }
    variant(name: $graphVariant) {
      id
      name
      isFederated
      hasManagedSubgraphs
      compositionVersion
      derivedVariantCount
      supportedDirectives {
        name
        enabled
      }
      buildConfig {
        federationVersion
        tagInApiSchema
      }
      allowedTracks {
        badge
        federationVersion
        compositionVersion
        deprecatedAt
        displayName
        minimumGatewayVersion
        minimumRouterVersion
        notSupportedAt
      }
      ...ConfigureBuildPipelineModalFragment
    }
    variants {
      id
      hasManagedSubgraphs
    }
    graphCapabilities {
      minimumBuildPipelineTrack
    }
  }
  ${configureBuildPipelineModalFragment}
`;

export const GeneralGraphSettingsCard = ({
  service,
  refetchData,
}: {
  service: GraphQLTypes.GeneralGraphSettingsCardFragment;
  refetchData: () => void;
}) => {
  const client = useApolloClient();
  const {
    id,
    title,
    description,
    avatarUrl,
    roles,
    variants,
    variant,
    defaultBuildPipelineTrack,
    graphType,
  } = service;
  const location = useLocation();
  const { activeSchema } = useSchema();
  const typeMap = useMemo(
    () => activeSchema?.getTypeMap() || {},
    [activeSchema],
  );
  const avatarUpload =
    service?.avatarUpload?.__typename === 'MediaUploadInfo' &&
    service.avatarUpload;

  const showDefaultBuildConfigSection =
    service.graphType === GraphQLTypes.GraphType.CLOUD_SUPERGRAPH;

  const allowedTracksNonNull = useMemo(
    () => variant?.allowedTracks.filter((t): t is NonNullable<typeof t> => !!t),
    [variant],
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>General settings</CardTitle>
          <CardDescription>
            Settings that apply to the whole graph and all its variants.
          </CardDescription>
          <CardHeaderControl>
            {avatarUrl && <Avatar variant="graph" src={avatarUrl} size="sm" />}
            <PermissionWarning
              graphId={id}
              graphPermissions="canUpdateAvatar"
              childRenderer={(hasPermission) => (
                <Button
                  variant="secondary"
                  isDisabled={!hasPermission || !avatarUpload}
                  as={Link}
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.updateGraphAvatar,
                  })}
                >
                  Upload image
                </Button>
              )}
            />
          </CardHeaderControl>
        </CardHeader>
        <CardBody>
          <CardSection className="flex-row">
            <Group orientation="vertical">
              <CardSectionTitle>Graph ID</CardSectionTitle>
              <CardSectionDescription>{id}</CardSectionDescription>
            </Group>
            <CardSectionControl>
              <Tooltip label="You cannot change a graph's ID once it is created. If you need to use a different ID, please make a new graph.">
                <Button variant="secondary" isDisabled>
                  Change ID
                </Button>
              </Tooltip>
            </CardSectionControl>
          </CardSection>
          <CardSection className="flex-row">
            <Group orientation="vertical">
              <CardSectionTitle>Title</CardSectionTitle>
              <CardSectionDescription>{title}</CardSectionDescription>
            </Group>
            <CardSectionControl>
              <PermissionWarning
                graphId={id}
                graphPermissions="canUpdateTitle"
                childRenderer={(hasPermission) => (
                  <Button
                    isDisabled={!hasPermission}
                    variant="secondary"
                    as={Link}
                    to={setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.graphSettingsTitleUpdate,
                    })}
                  >
                    Change title
                  </Button>
                )}
              />
            </CardSectionControl>
          </CardSection>
          <CardSection className="flex-row">
            <Group orientation="vertical">
              <CardSectionTitle>Description</CardSectionTitle>
              <CardSectionDescription>
                {description ? (
                  <DescriptionMarkdownRenderer
                    descriptionMarkdown={description}
                    typeMap={typeMap}
                  />
                ) : (
                  'No description set.'
                )}
              </CardSectionDescription>
            </Group>
            <CardSectionControl>
              <PermissionWarning
                graphId={id}
                graphPermissions="canUpdateDescription"
                childRenderer={(hasPermission) => (
                  <Button
                    isDisabled={!hasPermission}
                    variant="secondary"
                    as={Link}
                    to={setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.graphSettingsDescriptionUpdate,
                    })}
                  >
                    Change description
                  </Button>
                )}
              />
            </CardSectionControl>
          </CardSection>
          {showDefaultBuildConfigSection && (
            <CardSection className="flex-row">
              <Group orientation="vertical">
                <CardSectionTitle>Default build configuration</CardSectionTitle>
                <CardSectionDescription>
                  {
                    // if at least one variant hasManagedSubgraphs, then this graph is federated
                    !variants.some((v) => v.hasManagedSubgraphs) ? (
                      <>
                        This graph has no federated variants.{' '}
                        <ClickableText
                          as="a"
                          href="https://www.apollographql.com/docs/federation/managed-federation/setup"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-link"
                        >
                          Learn more about how to federate your graph
                        </ClickableText>
                        .
                      </>
                    ) : defaultBuildPipelineTrack ? (
                      `Federation ${defaultBuildPipelineTrack}`
                    ) : (
                      'No version set.'
                    )
                  }
                </CardSectionDescription>
              </Group>
              <CardSectionControl>
                <Tooltip
                  disabled={
                    !(
                      !roles?.canManageBuildConfig ||
                      service.graphType ===
                        GraphQLTypes.GraphType.CLOUD_SUPERGRAPH
                    )
                  }
                  label={
                    service.graphType ===
                    GraphQLTypes.GraphType.CLOUD_SUPERGRAPH
                      ? "You can't change the build configuration on cloud supergraphs. Apollo manages this for you."
                      : 'You do not have permission to edit the default build config'
                  }
                >
                  <Button
                    isDisabled={
                      !roles?.canManageBuildConfig ||
                      service.graphType ===
                        GraphQLTypes.GraphType.CLOUD_SUPERGRAPH
                    }
                    variant="secondary"
                    as={Link}
                    to={setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.buildConfig,
                    })}
                  >
                    Edit configuration
                  </Button>
                </Tooltip>
              </CardSectionControl>
            </CardSection>
          )}
        </CardBody>
      </Card>

      {avatarUpload && (
        <URLModal name={Config.modals.updateGraphAvatar}>
          {({ isOpen, closeModal }) => (
            <AvatarModal
              isOpen={isOpen}
              title="Update Graph Avatar"
              onClose={closeModal}
              avatarUploadUrl={avatarUpload.url}
              avatarUploadToken={avatarUpload.csrfToken}
              refreshAvatar={() => {
                client.cache.evict({
                  id: client.cache.identify({
                    id,
                    __typename: 'Service',
                  }),
                  fieldName: 'avatarUrl',
                });
              }}
            />
          )}
        </URLModal>
      )}
      <URLModal name={Config.modals.graphSettingsTitleUpdate}>
        {({ isOpen, closeModal }) => (
          <GraphTitleModal
            isOpen={isOpen}
            graphId={id}
            graphTitle={title}
            onClose={closeModal}
          />
        )}
      </URLModal>
      <URLModal name={Config.modals.graphSettingsDescriptionUpdate}>
        {({ isOpen, closeModal }) => (
          <GraphDescriptionModal
            isOpen={isOpen}
            graphId={id}
            graphDescription={description ?? ''}
            onClose={closeModal}
          />
        )}
      </URLModal>
      {variant &&
        allowedTracksNonNull &&
        service.graphType !== GraphQLTypes.GraphType.CLOUD_SUPERGRAPH && (
          <LazyUrlModal name={Config.modals.configureBuildPipeline}>
            {({ closeModal, isOpen }) => (
              <ConfigureBuildPipelineModal
                isGraph
                isOpen={isOpen}
                serviceId={id}
                variant={variant}
                onClose={closeModal}
                refetchGraphData={refetchData}
                graphType={graphType}
                allowedTracks={allowedTracksNonNull}
              />
            )}
          </LazyUrlModal>
        )}
    </>
  );
};

const UpdateDescriptionGraphMutation = gql`
  mutation Graph__UpdateDescriptionGraphMutation(
    $serviceId: ID!
    $description: String!
  ) {
    service(id: $serviceId) {
      updateDescription(description: $description) {
        id
        description
      }
    }
  }
`;

const GraphDescriptionModal = ({
  isOpen,
  graphId,
  graphDescription,
  onClose,
}: {
  isOpen: boolean;
  graphId: string;
  graphDescription: string;
  onClose: () => void;
}) => {
  const [updateDescription, { loading, error }] = useMutation<
    GraphQLTypes.Graph__UpdateDescriptionGraphMutation,
    GraphQLTypes.Graph__UpdateDescriptionGraphMutationVariables
  >(UpdateDescriptionGraphMutation, {
    onCompleted: () => onClose(),
  });
  const [newDescription, setNewDescription] = useState(graphDescription);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
    setNewDescription(event.target.value);

  const handleSubmit = () => {
    updateDescription({
      variables: {
        serviceId: graphId,
        description: newDescription,
      },
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <Form onSubmit={handleSubmit} role="dialog" noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Update Graph Description</ModalTitle>
            <ModalDescription>
              Changing your graph's description will not affect your data.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel>New graph description</FormLabel>
              <Textarea
                autoFocus
                size="lg"
                isDisabled={loading}
                name="description"
                onChange={handleChange}
                value={newDescription}
              />
            </FormControl>
            {error && <ErrorMessage error={error} />}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                isDisabled={loading}
                onClick={onClose}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={loading}
                loadingText="Change Graph Description"
                type="submit"
              >
                Change Graph Description
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};

const UpdateTitleGraphMutation = gql`
  mutation Graph__UpdateTitleGraphMutation($graphId: ID!, $title: String!) {
    service(id: $graphId) {
      updateTitle(title: $title) {
        id
        title
      }
    }
  }
`;

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required('Please provide a graph title.')
    .max(64, 'Graph titles must have at most 64 characters.'),
});

export const GraphTitleModal = ({
  isOpen,
  graphId,
  graphTitle,
  onClose,
}: {
  isOpen: boolean;
  graphId: string;
  graphTitle: string;
  onClose: () => void;
}) => {
  const [updateServiceTitle, { error }] = useMutation<
    GraphQLTypes.Graph__UpdateTitleGraphMutation,
    GraphQLTypes.Graph__UpdateTitleGraphMutationVariables
  >(UpdateTitleGraphMutation, {
    onCompleted: () => onClose(),
  });

  const {
    errors,
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting: loading,
  } = useFormik<Yup.InferType<typeof validationSchema>>({
    enableReinitialize: true,
    initialValues: {
      title: graphTitle,
    },
    async onSubmit(formValues) {
      try {
        await updateServiceTitle({
          variables: { graphId, title: formValues.title },
        });
      } catch {
        // We don't need to do anything to handle this here since the error will
        // come back in the `error` from our useMutation hook
      }
    },
    validationSchema,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <Form onSubmit={handleSubmit} role="dialog" noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Update Graph Title</ModalTitle>
            <ModalDescription>
              Changing your graph's title will not affect your data.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            <FormControl isInvalid={!!errors.title}>
              <FormLabel>New graph title</FormLabel>
              <Input
                autoFocus
                isDisabled={loading}
                name="title"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.title}
              />
              <FormErrorMessage>{errors.title}</FormErrorMessage>
            </FormControl>
            {error && (
              <ErrorMessage>
                <ApolloErrorMessage error={error} /> (special characters are not
                allowed)
              </ErrorMessage>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                isDisabled={loading}
                onClick={onClose}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={loading}
                loadingText="Change Graph Title"
                type="submit"
              >
                Change Graph Title
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
